import type {TextLineStylesObject, TextStyleRunObject} from '@PosterWhiteboard/items/text-item/text-style-runs.types';

/**
 * Why a text item with per-selection or per-line styling has to be rasterized for the vector PDF
 * path instead of going through `toSVG()`. Fabric exports style runs as one `tspan` per style chunk
 * and the fork's line offsets honor per-line alignment, so those stay vector; the reasons below
 * are the gaps the SVG export still has.
 */
export enum TextPdfRasterizeReason {
  /** The fork draws list markers in its canvas render only; the SVG export emits no marker glyphs. */
  LIST_MARKERS = 'list-markers',
  /** A per-character gradient fill references a `<defs>` entry the SVG export never writes. */
  RUN_GRADIENT_FILL = 'run-gradient-fill',
  /** svg-to-pdfkit mishandles stroked text; the whole-box outline rasterizes for the same reason. */
  RUN_OUTLINE = 'run-outline',
  /** Fabric exports shadows at object level only; a run's shadow would be dropped. */
  RUN_SHADOW = 'run-shadow',
  /** Same rules as the whole box: fabric breaks decorations per character under letter spacing, and underline plus line-through together. */
  RUN_DECORATIONS = 'run-decorations',
}

export interface TextPdfSafetyBaseStyles {
  letterSpacing: number;
  underLine: boolean;
  lineThrough: boolean;
}

export interface TextPdfSafetyInput {
  styleRuns: TextStyleRunObject[];
  lineStyles: Record<string, TextLineStylesObject>;
  baseStyles: TextPdfSafetyBaseStyles;
}

/** `null` when the item's rich-text styling survives the SVG export and the item may stay vector in the PDF. */
export const getRichTextPdfRasterizeReason = ({styleRuns, lineStyles, baseStyles}: TextPdfSafetyInput): TextPdfRasterizeReason | null => {
  if (hasListRow(lineStyles)) {
    return TextPdfRasterizeReason.LIST_MARKERS;
  }
  if (styleRuns.some((run) => run.styles.fillGradient !== null)) {
    return TextPdfRasterizeReason.RUN_GRADIENT_FILL;
  }
  if (styleRuns.some((run) => run.styles.outline?.isEnabled === true)) {
    return TextPdfRasterizeReason.RUN_OUTLINE;
  }
  if (styleRuns.some((run) => run.styles.shadow !== null)) {
    return TextPdfRasterizeReason.RUN_SHADOW;
  }
  if (styleRuns.some((run) => hasIncompatibleDecorations(run, baseStyles))) {
    return TextPdfRasterizeReason.RUN_DECORATIONS;
  }
  return null;
};

const hasListRow = (lineStyles: Record<string, TextLineStylesObject>): boolean => {
  return Object.values(lineStyles).some((line) => line.list !== null);
};

const hasIncompatibleDecorations = (run: TextStyleRunObject, baseStyles: TextPdfSafetyBaseStyles): boolean => {
  const underLine = run.styles.underLine ?? baseStyles.underLine;
  const lineThrough = run.styles.lineThrough ?? baseStyles.lineThrough;
  const hasDecoration = underLine || lineThrough;
  return (baseStyles.letterSpacing > 0 && hasDecoration) || (underLine && lineThrough);
};
